import {createStyles, Group, Text, Title} from '@mantine/core';
import React, {useCallback, useEffect, useState} from 'react';

import AaSequenceStore from 'molbio/aa-sequences/AaSequenceStore';
import ProteinConstants, {
  Protein,
  ProteinAnnotationVersion,
} from 'molbio/aa-sequences/util/AaSequenceConstants';
import * as sequtil from 'molbio/SeqUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import {ColorSwatch} from 'ui-library/ColorSwatch';
import useStoreSelector from 'ui-library/hooks/useStoreSelector';
import {
  RowSelectionMode,
  ScrollableTableColumn,
  ScrollableTableRow,
  TABLE_KEYS,
} from 'ui-library/scrollable/ScrollableTableConstants';
import SortableScrollableTable from 'ui-library/scrollable/SortableScrollableTable';

const useStyles = createStyles((theme) => ({
  tabContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  table: {
    height: '100%',
  },
  notesCell: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}));

type AaAnnotationTableSortKey = 'name' | 'type' | 'start' | 'end' | 'length' | 'color' | 'notesSort';

interface AaAnnotationTableColumn extends ScrollableTableColumn {
  accessorKey: keyof AaAnnotationRowData;
  sortKey?: AaAnnotationTableSortKey;
}

const AA_ANNOTATION_COLUMNS: Array<AaAnnotationTableColumn> = [
  {
    header: 'Name',
    accessorKey: 'name',
    isSortable: true,
    sortKey: 'name',
  },
  {
    header: 'Type',
    accessorKey: 'type',
    isSortable: true,
    sortKey: 'type',
  },
  {
    header: 'Start',
    accessorKey: 'displayStart',
    isSortable: true,
    sortKey: 'start',
  },
  {
    header: 'End',
    accessorKey: 'displayEnd',
    isSortable: true,
    sortKey: 'end',
  },
  {
    header: 'Length',
    accessorKey: 'length',
    isSortable: true,
    sortKey: 'length',
  },
  {
    header: 'Color',
    accessorKey: 'colorElement',
    isSortable: true,
    sortKey: 'color',
  },
  {
    header: 'Notes',
    accessorKey: 'notesElement',
    isSortable: true,
    sortKey: 'notesSort',
  },
];

interface AaAnnotationRowData {
  id: string;
  name: string;
  type: string;
  start: number;
  displayStart: number;
  end: number;
  displayEnd: number;
  length: number;
  color: string;
  colorElement: JSX.Element;
  notesElement: JSX.Element;
  notesSort: string;
}

interface AaAnnotationRow extends ScrollableTableRow {
  data: AaAnnotationRowData;
}

function getAnnotationRow({
  annotation,
  selectedIds,
  sequenceLength,
  classes,
}: {
  annotation: ProteinAnnotationVersion;
  selectedIds: Array<string>;
  sequenceLength: number;
  classes: ReturnType<typeof useStyles>['classes'];
}): AaAnnotationRow {
  const displayStart = sequtil.modelToDisplay(annotation.start, ProteinConstants.DEFAULT_START_INDEX);
  const displayEnd = sequtil.modelEndToDisplay(
    annotation.end,
    sequenceLength,
    ProteinConstants.DEFAULT_START_INDEX
  );
  const length = sequtil.distance(annotation.start, annotation.end, sequenceLength, false);

  return {
    id: annotation.id,
    isSelected: selectedIds.includes(annotation.id),
    data: {
      id: annotation.id,
      name: annotation.name,
      type: annotation.type,
      start: annotation.start,
      displayStart,
      end: annotation.end,
      displayEnd,
      length,
      color: annotation.color,
      colorElement: <ColorSwatch fillColor={annotation.color} darkerBorder={false} small={true} />,
      notesElement: <div className={classes.notesCell}>{annotation.notes}</div>,
      notesSort: annotation.notes,
    },
  };
}

function getDataFromAaSequenceStore(
  aaSequenceStore: typeof AaSequenceStore,
  aaSequenceId: string
): {
  aaSequence?: Protein;
  canEditFeatures: boolean;
} {
  const aaSequence = aaSequenceStore.getAaSequenceById(aaSequenceId);
  const canEditFeatures =
    (aaSequence?.permissions && aaSequence?.permissions.polymerFeaturesEditable && aaSequence?.isLatest) ??
    false;
  return {aaSequence, canEditFeatures};
}

export interface AaSequenceExpandedAnnotationTabProps {
  aaSequenceId: string;
}

export default function AaSequenceExpandedAnnotationTab({
  aaSequenceId,
}: AaSequenceExpandedAnnotationTabProps): JSX.Element {
  const {classes} = useStyles();
  const [selectedIds, setSelectedIds] = useState<Array<string>>([]);
  const {aaSequence, canEditFeatures} = useStoreSelector(
    AaSequenceStore,
    useCallback(
      (store: typeof AaSequenceStore) => getDataFromAaSequenceStore(store, aaSequenceId),
      [aaSequenceId]
    )
  );

  useEffect(() => {
    if (!canEditFeatures) {
      setSelectedIds([]);
    }
  }, [canEditFeatures]);

  useEffect(() => {
    if (aaSequence == null) {
      return;
    }
    setSelectedIds((previousSelectedIds) => {
      const annotationIds = new Set(aaSequence.version.annotations.map((a) => a.id));
      const validIds = previousSelectedIds.filter((id) => annotationIds.has(id));
      return validIds.length === previousSelectedIds.length ? previousSelectedIds : validIds;
    });
  }, [aaSequence]);

  function handleSelection(toggledRows: Array<AaAnnotationRow>, isSelected: boolean): void {
    const toggledIds = toggledRows.map((r: AaAnnotationRow) => r.id);
    if (isSelected) {
      const newlySelected = toggledIds.filter((id) => !selectedIds.includes(id));
      setSelectedIds([...selectedIds, ...newlySelected]);
    } else {
      setSelectedIds(selectedIds.filter((id) => !toggledIds.includes(id)));
    }
  }

  if (aaSequence == null) {
    return <></>;
  }

  const annotations = aaSequence.version.annotations;
  const sequenceLength = aaSequence.version.aminoAcids.length;
  const rows = annotations.map((annotation) =>
    getAnnotationRow({
      annotation,
      selectedIds,
      sequenceLength,
      classes,
    })
  );

  return (
    <div
      className={classes.tabContainer}
      {...getTestProps({component: 'AaSequenceExpandedAnnotationTab', element: 'root'})}
    >
      <Group position="apart" mb="sm">
        <Group spacing="sm" align="baseline">
          <Title order={5}>Annotations</Title>
          <Text size="sm" color="dimmed">
            {annotations.length} total
          </Text>
        </Group>
      </Group>
      <SortableScrollableTable
        className={classes.table}
        canResizeColumns={true}
        isCompact={true}
        columns={AA_ANNOTATION_COLUMNS}
        emptyTableText="This AA sequence has no annotations"
        initialRows={rows}
        initialSort={[{sortKey: 'start'}]}
        getSortValue={(row: AaAnnotationRow, sortKey: AaAnnotationTableSortKey) => row.data[sortKey]}
        tableKey={TABLE_KEYS.EXPANDED_AA_ANNOTATION_TABLE}
        rowSelectionMode={RowSelectionMode.MULTIPLE}
        disableSelection={!canEditFeatures}
        onRowsSelect={handleSelection}
        matchParentHeight={true}
      />
    </div>
  );
}
